import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const ER = "#D4537E";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const T = "#FFC1CD", TL = "#FFE4EA", TD = "#4B1F4F";
const GD = "#4B1F4F", GR = "#856589", GL = "#FAF7F5", WH = "#FFFFFF";

type Props = {
  open: boolean;
  onClose: () => void;
  onGeneratePDF: () => void; // opens the PDF in a new window for save/print
  user: any;
  daysTracked: number;
};

type DoctorRow = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  practice_name: string;
  specialty: string;
};

export default function SendToDoctorModal({ open, onClose, onGeneratePDF, user, daysTracked }: Props) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    practice_name: "",
    specialty: "",
  });
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"form" | "ready">("form");
  const [savedDoctor, setSavedDoctor] = useState<DoctorRow | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLLabelElement>(null);

  // Load most recent doctor for pre-fill
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      const { data } = await supabase
        .from("doctors_directory")
        .select("id, first_name, last_name, email, practice_name, specialty")
        .eq("referring_user_id", authUser.id)
        .order("last_sent_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data) {
        setForm({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          practice_name: data.practice_name,
          specialty: data.specialty || "",
        });
      }
    })();
  }, [open]);

  if (!open) return null;

  const update = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const markTouched = (k: string) => setTouched(t => ({ ...t, [k]: true }));

  const errors: Record<string, string> = {};
  if (!form.first_name.trim()) errors.first_name = "Please enter the doctor's first name.";
  if (!form.last_name.trim()) errors.last_name = "Please enter the doctor's last name.";
  if (!form.email.trim()) errors.email = "Please enter the doctor's email address.";
  else if (!EMAIL_RE.test(form.email.trim())) errors.email = "Please enter a valid email address (e.g., dr.smith@clinic.com).";
  if (!consent) errors.consent = "You must consent to share your health summary to continue.";

  const valid = Object.keys(errors).length === 0;

  const handleSubmit = async () => {
    setError(null);
    if (!valid) {
      setTouched({ first_name: true, last_name: true, email: true, consent: true });
      const target =
        errors.first_name ? firstNameRef.current :
        errors.last_name ? lastNameRef.current :
        errors.email ? emailRef.current :
        errors.consent ? consentRef.current : null;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      (target as HTMLInputElement | null)?.focus?.();
      return;
    }
    setSaving(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Not signed in");

      // Look for existing doctor (by email) to avoid duplicates
      const { data: existing } = await supabase
        .from("doctors_directory")
        .select("id")
        .eq("referring_user_id", authUser.id)
        .eq("email", form.email.trim().toLowerCase())
        .maybeSingle();

      const payload = {
        referring_user_id: authUser.id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        practice_name: form.practice_name.trim(),
        specialty: form.specialty.trim(),
        consent_given: true,
        last_sent_at: new Date().toISOString(),
      };

      let row: DoctorRow | null = null;
      if (existing) {
        const { data, error: upErr } = await supabase
          .from("doctors_directory")
          .update(payload)
          .eq("id", existing.id)
          .select()
          .maybeSingle();
        if (upErr) throw upErr;
        row = data as DoctorRow;
      } else {
        const { data, error: insErr } = await supabase
          .from("doctors_directory")
          .insert(payload)
          .select()
          .maybeSingle();
        if (insErr) throw insErr;
        row = data as DoctorRow;
      }
      setSavedDoctor(row);
      setStep("ready");
    } catch (e: any) {
      setError(e.message || "Something went wrong saving the doctor.");
    } finally {
      setSaving(false);
    }
  };

  const openPdf = () => onGeneratePDF();

  const openMail = () => {
    if (!savedDoctor) return;
    const patientName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "";
    const doctorName = `Dr. ${savedDoctor.first_name} ${savedDoctor.last_name}`.trim();

    const days = Math.max(0, Math.floor(Number(daysTracked) || 0));
    let reportLabel: string;
    let reportSubject: string;
    let bullets: string[];
    if (days < 14) {
      reportLabel = "my early KnowMeno tracking summary";
      reportSubject = "early tracking summary";
      bullets = [
        "A summary of my early symptom logging",
        "Daily check-ins from my first 2 weeks of tracking",
      ];
    } else if (days < 30) {
      reportLabel = "my 2-week KnowMeno symptom snapshot";
      reportSubject = "2-week snapshot";
      bullets = [
        "A clinician summary of my early symptom patterns",
        "Daily symptom averages from my first 2 weeks",
        "Initial symptom direction observations",
      ];
    } else if (days < 60) {
      reportLabel = "my 30-day KnowMeno symptom snapshot";
      reportSubject = "30-day snapshot";
      bullets = [
        "A clinician summary of my symptom patterns",
        "Daily symptom averages over the last 30 days",
        "Clinical flags from my recent data",
        "HRT adherence and response (if applicable)",
        "Lab values (if uploaded)",
      ];
    } else if (days < 90) {
      reportLabel = "my 60-day KnowMeno symptom snapshot";
      reportSubject = "60-day snapshot";
      bullets = [
        "A clinician summary of my symptom patterns",
        "Daily symptom averages over the last 30 days",
        "Clinical flags from my recent data",
        "Symptom direction (30 vs 30 day comparison)",
        "HRT adherence and response (if applicable)",
        "Lab values (if uploaded)",
      ];
    } else {
      reportLabel = "my 90-day KnowMeno Symptom Intelligence Report";
      reportSubject = "90-day Symptom Intelligence Report";
      bullets = [
        "A clinician summary of my symptom patterns",
        "Daily symptom averages over the last 30 days",
        "Clinical flags from my recent data",
        "HRT adherence and response (if applicable)",
        "Lab values (if uploaded)",
        "A 90-day symptom trend overview",
      ];
    }

    const subject = encodeURIComponent(
      `KnowMeno ${reportSubject} — ${patientName || "patient"}`
    );
    const body = encodeURIComponent(
      `Hello ${doctorName},\n\n` +
        `I'm tracking my menopause symptoms using KnowMeno and would like to share ${reportLabel} with you ahead of our appointment.\n\n` +
        `IMPORTANT: I've saved my report as a PDF and will attach it to this email before sending. If you don't see an attachment, please let me know.\n\n` +
        `My report includes:\n\n` +
        bullets.map(b => `- ${b}`).join("\n") + `\n\n` +
        `Looking forward to discussing.\n\n` +
        `Best,\n${patientName || ""}`
    );
    window.location.href = `mailto:${savedDoctor.email}?subject=${subject}&body=${body}`;
  };


  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: WH, width: "100%", maxWidth: 460, maxHeight: "92vh",
          borderRadius: "20px 20px 0 0", overflowY: "auto", padding: "20px 20px 28px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: T, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
              Send to my doctor
            </div>
            <div style={{ fontSize: 19, fontWeight: 500, color: GD, lineHeight: 1.3 }}>
              {step === "form" ? "Where should we send your report?" : "Almost there"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: GL, border: "none", borderRadius: 10, padding: "5px 11px", color: GD, fontSize: 13, cursor: "pointer" }}>
            Close
          </button>
        </div>

        {step === "form" && (
          <>
            <div style={{ fontSize: 13, color: GR, lineHeight: 1.55, marginBottom: 6 }}>
              We'll save your doctor's details so next time it's just one tap. We'll prepare your PDF for you to attach in your email app.
            </div>
            <div style={{ fontSize: 12, color: GR, marginBottom: 16 }}>
              Fields marked with <span style={{ color: ER }}>*</span> are required.
            </div>

            <Field label="First name" required error={touched.first_name ? errors.first_name : undefined}>
              <input
                ref={firstNameRef}
                value={form.first_name}
                onChange={e => update("first_name", e.target.value)}
                onBlur={() => markTouched("first_name")}
                style={inputStyle}
              />
            </Field>
            <Field label="Last name" required error={touched.last_name ? errors.last_name : undefined}>
              <input
                ref={lastNameRef}
                value={form.last_name}
                onChange={e => update("last_name", e.target.value)}
                onBlur={() => markTouched("last_name")}
                style={inputStyle}
              />
            </Field>
            <Field label="Email" required error={touched.email ? errors.email : undefined}>
              <input
                ref={emailRef}
                type="email"
                value={form.email}
                onChange={e => update("email", e.target.value)}
                onBlur={() => markTouched("email")}
                style={inputStyle}
                placeholder="dr.smith@clinic.com"
              />
            </Field>
            <Field label="Practice or clinic name" optional>
              <input value={form.practice_name} onChange={e => update("practice_name", e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Specialty" optional>
              <input value={form.specialty} onChange={e => update("specialty", e.target.value)} style={inputStyle} placeholder="e.g. Gynecology, Menopause specialist" />
            </Field>

            <label
              ref={consentRef}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px",
                background: consent ? TL : GL, borderRadius: 12, marginTop: 6, marginBottom: 6, cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={e => { setConsent(e.target.checked); markTouched("consent"); }}
                style={{ marginTop: 2, accentColor: T }}
              />
              <span style={{ fontSize: 12, color: consent ? TD : GD, lineHeight: 1.5 }}>
                I consent to sharing my KnowMeno health summary with this doctor for the purpose of my care. <span style={{ color: ER }}>*</span>
              </span>
            </label>
            {touched.consent && errors.consent && (
              <div style={{ color: ER, fontSize: 12, marginBottom: 10, marginTop: 2 }}>{errors.consent}</div>
            )}
            {!(touched.consent && errors.consent) && <div style={{ marginBottom: 8 }} />}

            {error && <div style={{ background: "#FFE4EA", color: "#8A1B2A", padding: "10px 12px", borderRadius: 10, fontSize: 12, marginBottom: 10 }}>{error}</div>}

            <button
              onClick={handleSubmit}
              disabled={saving}
              style={{
                width: "100%", padding: 14, borderRadius: 14, border: "none", fontSize: 15, fontWeight: 500,
                background: valid && !saving ? T : GL, color: valid && !saving ? WH : GR,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Continue"}
            </button>
          </>
        )}

        {step === "ready" && savedDoctor && (
          <>
            <div style={{ fontSize: 13, color: GR, lineHeight: 1.6, marginBottom: 12 }}>
              We've saved <strong style={{ color: GD, fontWeight: 500 }}>Dr. {savedDoctor.first_name} {savedDoctor.last_name}</strong> to your contacts. Two quick steps:
            </div>

            <div style={{
              background: TL, color: TD, borderRadius: 12, padding: "12px 14px",
              fontSize: 12, lineHeight: 1.55, marginBottom: 14, fontWeight: 500,
            }}>
              The PDF isn't attached automatically — you'll need to attach it manually in your email app before sending.
            </div>

            <Step n={1} title="Download your Symptom Intelligence Report PDF">
              Tap the button below. A new window opens with your health summary — use your browser to <em>Save as PDF</em> (or print to PDF) to your device.
              <button onClick={openPdf} style={primaryBtn}>Open PDF preview →</button>
            </Step>

            <Step n={2} title="Open your email and attach the PDF">
              We'll pre-fill an email to <strong style={{ color: GD, fontWeight: 500 }}>{savedDoctor.email}</strong>. <strong style={{ color: GD, fontWeight: 600 }}>Attach the PDF you just downloaded</strong>, then send to your doctor.
              <button onClick={openMail} style={{ ...primaryBtn, background: WH, color: T, border: `1.5px solid ${T}` }}>
                Open email to Dr. {savedDoctor.last_name} →
              </button>
            </Step>


            <button onClick={onClose} style={{ width: "100%", padding: 12, borderRadius: 14, background: GL, color: GD, border: "none", fontSize: 14, marginTop: 10, cursor: "pointer" }}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Field({
  label, children, required, optional, error,
}: { label: string; children: React.ReactNode; required?: boolean; optional?: boolean; error?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: GR, marginBottom: 6 }}>
        {label}
        {required && <span style={{ color: ER, marginLeft: 2 }}>*</span>}
        {optional && <span style={{ color: GR, marginLeft: 4, opacity: 0.7 }}>(optional)</span>}
      </div>
      {children}
      {error && <div style={{ color: ER, fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: GL, borderRadius: 14, padding: "14px 16px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: "50%", background: T, color: WH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 500 }}>{n}</div>
        <div style={{ fontSize: 14, fontWeight: 500, color: GD }}>{title}</div>
      </div>
      <div style={{ fontSize: 12, color: GR, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "11px 13px", borderRadius: 12, fontSize: 14,
  border: "1.5px solid #E8D5DA", color: GD, fontFamily: "inherit",
  boxSizing: "border-box", outline: "none", background: WH,
};

const primaryBtn: React.CSSProperties = {
  display: "block", width: "100%", marginTop: 10, padding: 12, borderRadius: 12,
  background: T, color: WH, border: "none", fontSize: 14, fontWeight: 500, cursor: "pointer",
};
